/**
 * ProfilePortfolio Component
 * 
 * Portfolio gallery for contractor profiles
 */

'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { buildStaticUrl } from '@/src/shared/api';
import type { ContractorProfile } from '@hummii/shared-types';

type PortfolioItemType = ContractorProfile['portfolio'][number];

export interface ProfilePortfolioProps {
  items: PortfolioItemType[];
  isOwnProfile: boolean;
  onAddItem?: () => void;
}

export function ProfilePortfolio({ items, isOwnProfile, onAddItem }: ProfilePortfolioProps) {
  const t = useTranslations('profile.portfolio');
  const [selectedItem, setSelectedItem] = useState<PortfolioItemType | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-background-card">
      <div className="flex items-center justify-between mb-5">
        <Typography variant="h2" weight="semibold">
          {t('title')}
        </Typography>
        {isOwnProfile && (
          <button
            onClick={onAddItem}
            className="flex items-center gap-2 text-text-primary hover:text-text-accent transition-colors"
          >
            <Icon name="edit" size="sm" color="primary" />
            <Typography variant="body" weight="medium">
              {t('edit')}
            </Typography>
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <Typography variant="body" color="tertiary" className="text-center py-8">
          {t('noItems')}
        </Typography>
      ) : (
        <>
          <div 
            className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mb-5 transition-all duration-300 ${
              !isExpanded && items.length > 4 ? 'max-h-[340px] overflow-hidden' : ''
            }`}
          >
            {(isExpanded ? items : items.slice(0, 4)).map((item) => (
              <div
                key={item.id}
                className="group relative aspect-square rounded-[10px] overflow-hidden cursor-pointer bg-background-secondary hover:shadow-lg transition-shadow"
                onClick={() => setSelectedItem(item)}
              >
                {/* Portfolio Image */}
                {item.images && item.images.length > 0 ? (
                  <Image
                    src={buildStaticUrl(item.images[0])}
                    alt={item.title}
                    fill
                    sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    className="object-cover"
                    unoptimized
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-background-tertiary">
                    <Icon name="clip" size="lg" color="tertiary" />
                  </div>
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                  <Typography variant="bodySm" color="inverse" truncate>
                    {item.title}
                  </Typography>
                </div>
              </div>
            ))}
          </div>

          {items.length > 4 && (
            <div className="flex justify-center">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="bg-background-accent hover:bg-background-accent-hover text-text-inverse font-medium text-[20px] leading-6 px-[79px] py-[15px] rounded-full transition-colors"
              >
                {isExpanded ? t('showLess') : t('viewAll')}
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal for selected item */}
      {selectedItem && (
        <div
          className="fixed inset-0 bg-background-overlay z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
        >
          <div
            className="bg-background-card rounded-[10px] max-w-2xl w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <Typography variant="h2" weight="semibold">
                {selectedItem.title}
              </Typography>
              <button
                onClick={() => setSelectedItem(null)}
                className="w-12 h-12 flex items-center justify-center hover:bg-background-secondary rounded-full transition-colors shadow-md"
              >
                <Icon name="x" size="md" color="primary" />
              </button>
            </div>

            {/* Portfolio Image */}
            <div className="relative aspect-video bg-background-secondary rounded-[10px] mb-4 overflow-hidden">
              {selectedItem.images && selectedItem.images.length > 0 ? (
                <Image
                  src={buildStaticUrl(selectedItem.images[0])}
                  alt={selectedItem.title}
                  fill
                  sizes="(max-width: 768px) 100vw, 672px"
                  className="object-contain"
                  unoptimized
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="clip" size="xl" color="tertiary" />
                </div>
              )}
            </div>

            {selectedItem.description && (
              <Typography variant="body" color="secondary">
                {selectedItem.description}
              </Typography>
            )}

            {selectedItem.completedAt && (
              <Typography variant="note" color="tertiary" className="mt-2">
                {t('completedOn', { 
                  date: new Date(selectedItem.completedAt).toLocaleDateString() 
                })}
              </Typography>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
